/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from "react";
import { URLS, makeApi } from "Utils/apiURL";
import { MAX_INPUT_ROWS } from "Pages/Dashboard/data-toolkit/constants";
import {
  parseDelimited,
  toHeadersAndRows,
  detectHeader,
  autoMap
} from "Pages/Dashboard/data-toolkit/csvUtils";
import { unwrap } from "Pages/Dashboard/data-toolkit/dtHelpers";
import ToolIcon from "Pages/Dashboard/data-toolkit/ToolIcon";
import JobsList from "Pages/Dashboard/data-toolkit/JobsList";
import ResultsDrawer from "Pages/Dashboard/data-toolkit/ResultsDrawer";
import ContactListSource, { CONTACT_LIST_TOOLS } from "Pages/Dashboard/data-toolkit/ContactListSource";

// Generic paste/CSV -> map columns -> run -> results flow for a Data Toolkit tool.
// Everything tool-specific is supplied via `cfg` (see FindEmail / ValidateEmails):
//   toolKey, iconKey, title, description, fields, endpoint, mappingHint,
//   requiredCheck(mapping), requiredError, rowCredits(row, mapping), costNote(max),
//   foundLabel, stats, columns, runNoun.
const ToolRunner = ({ cfg }) => {
  const [step, setStep] = useState("input"); // input | map
  const [jobName, setJobName] = useState("");
  const [mode, setMode] = useState("paste"); // paste | csv | list
  const listSupported = CONTACT_LIST_TOOLS.includes(cfg.toolKey);
  const [rawText, setRawText] = useState("");
  const [fileName, setFileName] = useState("");
  const [hasHeader, setHasHeader] = useState(true);
  const [parsed, setParsed] = useState({ headers: [], rows: [] });
  const [mapping, setMapping] = useState({});
  const [advanced, setAdvanced] = useState(false);
  const [config, setConfig] = useState(cfg.defaultConfig || {});
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);
  const [viewingJobId, setViewingJobId] = useState(null);

  // ---- input ----
  const readFile = file => {
    if (!file) return;
    setError("");
    const name = (file.name || "").toLowerCase();
    const looksCsv =
      name.endsWith(".csv") ||
      (file.type || "").includes("csv") ||
      (file.type || "").includes("text");
    if (!looksCsv) {
      setError("Please upload a .csv file.");
      return;
    }
    setFileName(file.name);
    if (!jobName) setJobName(file.name.replace(/\.[^.]+$/, ""));
    const reader = new FileReader();
    reader.onload = ev => {
      const text = ev.target.result || "";
      setRawText(text);
      goToMapping(text);
    };
    reader.readAsText(file);
  };

  const onFile = e => readFile(e.target.files && e.target.files[0]);
  const onDrop = e => {
    e.preventDefault();
    setDragOver(false);
    readFile(e.dataTransfer.files && e.dataTransfer.files[0]);
  };

  const goToMapping = text => {
    setError("");
    const matrix = parseDelimited(text);
    if (matrix.length === 0) {
      setError("No data rows found. Paste values or upload a CSV first.");
      return;
    }
    let detected = detectHeader(matrix);
    let hr = toHeadersAndRows(matrix, detected);
    // Safety net: if treating row 0 as a header leaves no data rows, it wasn't a
    // header — fall back to headerless so a single/short paste still works.
    if (hr.rows.length === 0 && detected) {
      detected = false;
      hr = toHeadersAndRows(matrix, false);
    }
    if (hr.rows.length === 0) {
      setError("No data rows found. Paste values or upload a CSV first.");
      return;
    }
    if (hr.rows.length > MAX_INPUT_ROWS) {
      setError(
        `That's ${hr.rows.length.toLocaleString()} records. The limit is ${MAX_INPUT_ROWS.toLocaleString()} per run, so please split the file.`
      );
      return;
    }
    setHasHeader(detected);
    setParsed(hr);
    setMapping(autoMap(hr.headers, hr.rows, cfg.fields));
    setStep("map");
  };

  const onContinue = () => goToMapping(rawText);

  const toggleHeader = () => {
    const next = !hasHeader;
    const matrix = parseDelimited(rawText);
    const hr = toHeadersAndRows(matrix, next);
    setHasHeader(next);
    setParsed(hr);
    setMapping(autoMap(hr.headers, hr.rows, cfg.fields));
  };

  // ---- mapping (column -> field) ----
  const fieldForColumn = colIndex => {
    const entry = Object.entries(mapping).find(([, v]) => v === colIndex);
    return entry ? entry[0] : "";
  };

  const setColumnField = (colIndex, fieldKey) => {
    setMapping(prev => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        if (v === colIndex) return;
        if (k === fieldKey) return;
        next[k] = v;
      });
      if (fieldKey) next[fieldKey] = colIndex;
      return next;
    });
  };

  const resetInput = () => {
    setStep("input");
    setJobName("");
    setRawText("");
    setFileName("");
    setParsed({ headers: [], rows: [] });
    setMapping({});
    setAdvanced(false);
    setConfig(cfg.defaultConfig || {});
    setError("");
  };

  const onRun = async () => {
    setError("");
    if (!cfg.requiredCheck(mapping)) {
      setError(cfg.requiredError);
      return;
    }
    setSubmitting(true);
    const payload = {
      name: jobName || fileName || cfg.runNoun,
      fileName,
      headers: parsed.headers,
      rows: parsed.rows,
      mapping,
      advancedFields: cfg.advancedOption ? advanced : false,
      ...(cfg.configPanel ? config : {})
    };
    const res = await makeApi({ url: cfg.endpoint, method: "POST", data: payload });
    setSubmitting(false);
    const { status, data } = unwrap(res);
    if (status === 200 && data && data.id) {
      resetInput();
      setRefreshKey(k => k + 1);
      setViewingJobId(data.id);
    } else {
      setError(
        (data && data.message) || "Could not start the run. Please try again."
      );
    }
  };

  const downloadJob = job => {
    const a = document.createElement("a");
    a.href = URLS.dataToolkitDownload.replace("<<id>>", job.id);
    a.download = `${job.name || cfg.runNoun}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // ------------------------------------------------------------------
  const renderInput = () => (
    <div className="dtCardPanel">
      <div className="dtInputForm">
        <div className="dtSectionTitle">Add your records</div>
        <div className="dtSectionSub">{cfg.inputSub}</div>

        <div className="dtField">
          <label>Name this run</label>
          <input
            className="dtInput"
            type="text"
            placeholder="e.g. Q3 outbound leads"
            value={jobName}
            onChange={e => setJobName(e.target.value)}
          />
        </div>

        <div className="dtTabs">
          <div
            className={`dtTab ${mode === "paste" ? "active" : ""}`}
            onClick={() => setMode("paste")}
          >
            Paste values
          </div>
          <div
            className={`dtTab ${mode === "csv" ? "active" : ""}`}
            onClick={() => setMode("csv")}
          >
            Upload CSV
          </div>
          {listSupported && (
            <div
              className={`dtTab ${mode === "list" ? "active" : ""}`}
              onClick={() => setMode("list")}
            >
              From a contact list
            </div>
          )}
        </div>

        {mode === "list" ? (
          <ContactListSource
            cfg={cfg}
            jobName={jobName}
            config={config}
            setConfig={setConfig}
            onStarted={job => {
              setJobName("");
              setRefreshKey(k => k + 1);
              setViewingJobId(job.id);
            }}
          />
        ) : mode === "paste" ? (
          <>
            <textarea
              className="dtTextarea"
              placeholder={cfg.pastePlaceholder}
              value={rawText}
              onChange={e => setRawText(e.target.value)}
            />
            <div className="dtDropSub">
              One record per line, up to {MAX_INPUT_ROWS.toLocaleString()} rows.
            </div>
          </>
        ) : (
          <label
            className={`dtFileDrop ${dragOver ? "dragging" : ""} ${
              fileName ? "hasFile" : ""
            }`}
            onDragOver={e => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={e => {
              e.preventDefault();
              setDragOver(false);
            }}
            onDrop={onDrop}
          >
            <span className="dtDropIcon">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
                strokeLinejoin="round">
                <path d="M12 16V5M8.5 8.5 12 5l3.5 3.5" />
                <path d="M20 15v3.5A1.5 1.5 0 0 1 18.5 20h-13A1.5 1.5 0 0 1 4 18.5V15" />
              </svg>
            </span>
            {fileName ? (
              <>
                <div className="dtDropFile">{fileName}</div>
                <div className="dtDropSub">Drop another file or click to replace</div>
              </>
            ) : (
              <>
                <div className="dtDropMain">
                  <b>Drag &amp; drop</b> a CSV here, or <b>click to browse</b>
                </div>
                <div className="dtDropSub">
                  .csv files, up to {MAX_INPUT_ROWS.toLocaleString()} rows
                </div>
              </>
            )}
            <input
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={onFile}
            />
          </label>
        )}

        {mode !== "list" && error && <div className="dtError">{error}</div>}

        {mode !== "list" && (mode === "paste" || rawText) && (
          <div className="dtActions">
            <button
              className="dtBtn primary"
              onClick={onContinue}
              disabled={mode === "csv" && !rawText}
            >
              Continue{mode === "csv" ? " to mapping" : ""}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderMap = () => {
    const previewRows = parsed.rows.slice(0, 5);
    const usable = cfg.requiredCheck(mapping);
    const advExtra =
      cfg.advancedOption && advanced ? cfg.advancedOption.extraCreditPerMatch : 0;
    const perRowCredits = usable
      ? parsed.rows.map(r => {
          const c = cfg.rowCredits(r, mapping);
          return c > 0 ? c + advExtra : 0;
        })
      : [];
    const skippedCount = perRowCredits.filter(c => c === 0).length;
    const usableRowCount = perRowCredits.filter(c => c > 0).length;
    // Some tools add cost from the config panel (e.g. Data Magic replacement
    // contacts) that isn't tied to a single row; fold it into the estimate.
    const configExtra = cfg.configCredits
      ? cfg.configCredits(config, usableRowCount)
      : 0;
    const maxCredits = perRowCredits.reduce((s, c) => s + c, 0) + configExtra;
    return (
      <div className="dtCardPanel">
        <div className="dtSectionTitle">Map your columns</div>
        <div className="dtSectionSub">
          {parsed.rows.length.toLocaleString()} record
          {parsed.rows.length === 1 ? "" : "s"} in “
          {jobName || fileName || "this run"}”. Pick what each column is. We've
          pre-filled the obvious ones.
        </div>

        <div className="dtHint">{cfg.mappingHint}</div>

        <div className="dtCheckRow">
          <input
            type="checkbox"
            checked={hasHeader}
            onChange={toggleHeader}
            id="dtHasHeader"
          />
          <label htmlFor="dtHasHeader">
            First row is a header {hasHeader ? "(detected)" : ""}
          </label>
        </div>

        {usable && skippedCount > 0 && (
          <div className="dtWarn">
            {skippedCount.toLocaleString()} of{" "}
            {parsed.rows.length.toLocaleString()} record
            {skippedCount === 1 ? "" : "s"} are missing the fields needed, so
            they'll be skipped and not charged.
          </div>
        )}

        {cfg.advancedOption && (
          <div className="dtCheckRow">
            <input
              type="checkbox"
              checked={advanced}
              onChange={e => setAdvanced(e.target.checked)}
              id="dtAdvanced"
            />
            <label htmlFor="dtAdvanced">
              {cfg.advancedOption.label}{" "}
              <span className="dtCreditBadge">{cfg.advancedOption.note}</span>
            </label>
          </div>
        )}

        {cfg.configPanel && cfg.configPanel(config, setConfig)}

        {usable && maxCredits > 0 && (
          <div className="dtCostNote">{cfg.costNote(maxCredits, advanced)}</div>
        )}

        <div className="dtMapTableWrap">
          <table className="dtMapTable">
            <thead>
              <tr className="dtMapHeadRow">
                {parsed.headers.map((h, i) => (
                  <th key={i} title={h}>
                    {h}
                  </th>
                ))}
              </tr>
              <tr className="dtMapSelectRow">
                {parsed.headers.map((h, i) => {
                  const val = fieldForColumn(i);
                  return (
                    <th key={i}>
                      <select
                        className={val ? "mapped" : ""}
                        value={val}
                        onChange={e => setColumnField(i, e.target.value)}
                      >
                        <option value="">Skip column</option>
                        {cfg.fields.map(f => (
                          <option key={f.key} value={f.key}>
                            {f.label}
                          </option>
                        ))}
                      </select>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {previewRows.map((row, ri) => (
                <tr key={ri}>
                  {parsed.headers.map((h, ci) => (
                    <td key={ci} title={row[ci] || ""}>
                      {row[ci] || <span className="dtEmptyCell">-</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {parsed.rows.length > previewRows.length && (
          <div className="dtPreviewNote">
            Showing first {previewRows.length} of {parsed.rows.length} records
          </div>
        )}

        {error && <div className="dtError">{error}</div>}

        <div className="dtActions">
          <button className="dtBtn primary" onClick={onRun} disabled={submitting}>
            {submitting ? "Starting…" : cfg.runLabel}
          </button>
          <button className="dtBtn ghost" onClick={() => setStep("input")}>
            Back
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="dtToolPage">
      <div className="dtToolHeader">
        <div className="dtToolIcon">
          <ToolIcon name={cfg.iconKey} size={26} />
        </div>
        <div>
          <h2>{cfg.title}</h2>
          <p>{cfg.description}</p>
        </div>
      </div>

      {step === "input" ? renderInput() : renderMap()}

      <JobsList
        tool={cfg.toolKey}
        foundLabel={cfg.foundLabel}
        refreshKey={refreshKey}
        onView={job => setViewingJobId(job.id)}
        onDownload={downloadJob}
      />

      {viewingJobId && (
        <ResultsDrawer
          jobId={viewingJobId}
          onClose={() => setViewingJobId(null)}
          onProcessed={() => setRefreshKey(k => k + 1)}
          stats={cfg.stats}
          columns={cfg.columns}
        />
      )}
    </div>
  );
};

export default ToolRunner;
